import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ReviewAddOnCarousal.module.css";
import { ProductObj, VariantsObj } from "../../Types";
import { getPriceLabel, getDiscountValueWithSign, DISCOUNT_TYPES } from "../../constants";
import AngleLeftIcon from "../../../../components/Icons/AngleLeftIcon";
import AngleRightIcon from "../../../../components/Icons/AngleRightIcon";
import { VariantSelectorV2 } from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";

interface ReviewAddOnCarouselProps {
    products: ProductObj[];
    selectedProducts: ProductObj[];
    bundleConfig: any;
    money_format: string;
    currency: string;
    onAddProduct: (product: ProductObj, variant: VariantsObj) => void;
    onProductRemove: (variantId: string) => void;
    standAlone?: boolean;
}

const MaxDotsPerPage = 20;

const ReviewAddOnCarousel = ({
    products,
    selectedProducts,
    bundleConfig,
    money_format,
    currency,
    onAddProduct,
    onProductRemove,
    standAlone = false,
}: ReviewAddOnCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const len = products.length;
    const [dotPage, setDotPage] = useState(0);
    const totalDots = Math.ceil(len / itemsPerView);
    const currentDotIndex = Math.floor(currentIndex / itemsPerView);
    const currentDotPage = Math.floor(currentDotIndex / MaxDotsPerPage);
    const startDot = dotPage * MaxDotsPerPage;
    const endDot = Math.min(startDot + MaxDotsPerPage, totalDots);

    // Track selected variants for each product
    const [selectedVariants, setSelectedVariants] = useState<Record<string, VariantsObj>>({});

    // Initialize selected variants when products change
    useEffect(() => {
        const initialSelected: Record<string, VariantsObj> = {};
        products.forEach((product) => {
            const key = product.id;
            if (!selectedVariants[key] && product.variants.length > 0) {
                const firstAvailable = product.variants[0];
                initialSelected[key] = firstAvailable;
            }
        });

        if (Object.keys(initialSelected).length > 0) {
            setSelectedVariants((prev) => ({ ...prev, ...initialSelected }));
        }
    }, [products]);

    const handleVariantChange = (productId: string, variant: VariantsObj) => {
        setSelectedVariants((prev) => ({
            ...prev,
            [productId]: variant,
        }));
    };

    useEffect(() => {
        if (dotPage !== currentDotPage) {
            setDotPage(currentDotPage);
        }
    }, [currentDotPage, dotPage]);

    useEffect(() => {
        setItemsPerView(1);
    }, []);

    // Move forward
    const nextSlide = useCallback(
        (e?: any) => {
            e?.preventDefault();
            e?.stopPropagation();
            setCurrentIndex((prevIndex) => (prevIndex + itemsPerView) % len);
        },
        [itemsPerView, len],
    );

    // Move backward
    const prevSlide = useCallback(
        (e?: any) => {
            e?.preventDefault();
            e?.stopPropagation();
            setCurrentIndex((prevIndex) => (prevIndex - itemsPerView + len) % len);
        },
        [itemsPerView, len],
    );

    if (!products || products.length === 0) return null;

    // Helper function to render variant selector
    const renderVariantSelector = (product: ProductObj) => {
        const selectedVariant = selectedVariants[product.id];

        return (
            <VariantSelectorV2
                variants={product.variants}
                selected={[selectedVariant]}
                handleChange={(variants: VariantsObj[]) => {
                    if (variants[0]) {
                        handleVariantChange(product.id, variants[0]);
                    }
                }}
                productCardIndex={0}
                options={product.options || []}
                standAlone={standAlone}
                bundlesDesign={bundleConfig?.bundles_design}
                bundleConfig={bundleConfig}
            />
        );
    };

    return (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="reviewProductCardAddonProducts">
            <div className={styles.addonCarouselWrapper}>
                {/* Title */}
                {bundleConfig?.review_step?.addon_section_title && (
                    <div
                        className={styles.addonCarouselTitle}
                        style={{
                            fontSize: bundleConfig?.bundles_design?.addon_title_text_size || "14px",
                            color: bundleConfig?.bundles_design?.addon_title_text_color || "#303030",
                        }}
                    >
                        {bundleConfig.review_step.addon_section_title}
                    </div>
                )}

                <div className={styles.slider} ref={containerRef}>
                    {len >= 2 && (
                        <span onClick={prevSlide} style={{ cursor: "pointer" }}>
                            <AngleLeftIcon size={15} color="#303030" />
                        </span>
                    )}
                    <div className={styles.slideContainer}>
                        {Array.from({ length: Math.min(itemsPerView, len) }).map((_, i) => {
                            const productIndex = (currentIndex + i) % len;
                            const product = products[productIndex];
                            const selectedVariant = selectedVariants[product.id];
                            const imageUrl =
                                selectedVariant?.image?.originalSrc ||
                                product?.variants?.[0]?.image?.originalSrc ||
                                product?.images?.[0]?.originalSrc;
                            const selectedAddon = selectedProducts.find((sp) => sp.id === product.id && sp.isAddon);
                            const quantity =
                                selectedAddon?.selectedVariants?.filter((v) => v.id === selectedVariant?.id)?.length ||
                                0;

                            const addonDiscountType = bundleConfig?.review_step?.discount_type;
                            const addonDiscountValue = Number(bundleConfig?.review_step?.discount_value) || 0;
                            const price = Number(selectedVariant?.price) || 0;
                            let finalPrice = price;
                            let hasDiscount = false;

                            if (!!addonDiscountType && addonDiscountValue > 0) {
                                if (addonDiscountType === "percentage") {
                                    finalPrice = price * (1 - addonDiscountValue / 100);
                                } else if (addonDiscountType === "flat") {
                                    finalPrice = Math.max(0, price - addonDiscountValue);
                                }
                                hasDiscount = true;
                            }

                            return (
                                <div
                                    key={productIndex}
                                    className={styles.slideItem}
                                    style={{
                                        background: bundleConfig?.bundles_design?.addon_card_bg_color,
                                        border: `1px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                        borderRadius: "8px",
                                        width: "100%",
                                    }}
                                >
                                    <div className={styles.topRow}>
                                        <div className={styles.upsellItemImageWrapper} title={product?.title}>
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={product.handle}
                                                    className={styles.productImage}
                                                />
                                            ) : (
                                                <div className={styles.addonProductImagePlaceholder}>No Image</div>
                                            )}
                                        </div>
                                        <div className={styles.upsellItemDetails}>
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey="productTitle"
                                            >
                                                <div
                                                    className={styles.upsellItemTitle}
                                                    style={{
                                                        color: bundleConfig?.bundles_design?.product_name_color,
                                                        fontSize: bundleConfig?.bundles_design?.product_name_size,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design?.product_name_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design?.product_name_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                    title={product?.title}
                                                >
                                                    {product?.title}
                                                </div>
                                            </InspectorEditableBlock>
                                            <div
                                                className={styles.upsellItemPriceWrapper}
                                                style={{
                                                    color: bundleConfig?.bundles_design?.highlighted_price_tag_color,
                                                }}
                                            >
                                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            gap: 5,
                                                            alignItems: "center",
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        <InspectorEditableBlock
                                                            bundleType={bundleConfig?.bundle_type}
                                                            settingKey="productPrice"
                                                        >
                                                            <span
                                                                className={styles.upsellItemPrice}
                                                                style={{
                                                                    fontSize:
                                                                        bundleConfig?.bundles_design
                                                                            ?.highlighted_price_tag_size,
                                                                    fontWeight:
                                                                        bundleConfig?.bundles_design?.highlighted_price_tag_style?.split(
                                                                            " ",
                                                                        )[0],
                                                                    fontStyle:
                                                                        bundleConfig?.bundles_design?.highlighted_price_tag_style?.split(
                                                                            " ",
                                                                        )[1],
                                                                }}
                                                            >
                                                                {bundleConfig?.review_step
                                                                    ?.apply_discount_on_addon_products &&
                                                                hasDiscount ? (
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "column",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                display: "flex",
                                                                                columnGap: "6px",
                                                                                rowGap: "0px",
                                                                                flexWrap: "wrap",
                                                                            }}
                                                                        >
                                                                            <span>
                                                                                {finalPrice == 0
                                                                                    ? "FREE"
                                                                                    : getPriceLabel(
                                                                                          finalPrice,
                                                                                          money_format,
                                                                                          currency,
                                                                                      )}
                                                                            </span>
                                                                            <span
                                                                                style={{
                                                                                    textDecoration: "line-through",
                                                                                    opacity: 0.5,
                                                                                }}
                                                                            >
                                                                                {getPriceLabel(
                                                                                    price,
                                                                                    money_format,
                                                                                    currency,
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        {bundleConfig?.review_step?.discount_type ===
                                                                            DISCOUNT_TYPES.PERCENTAGE && (
                                                                            <div
                                                                                style={{
                                                                                    color: "#2ea818",
                                                                                    fontSize: "12px",
                                                                                    fontWeight: "650",
                                                                                    textAlign: "left",
                                                                                }}
                                                                            >
                                                                                {getDiscountValueWithSign(
                                                                                    addonDiscountType,
                                                                                    addonDiscountValue,
                                                                                    currency,
                                                                                    money_format,
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    getPriceLabel(
                                                                        selectedVariant?.price,
                                                                        money_format,
                                                                        currency,
                                                                    )
                                                                )}
                                                            </span>
                                                        </InspectorEditableBlock>
                                                    </div>
                                                    {!!product?.discountType?.length &&
                                                        product?.discountType != "none" && (
                                                            <div
                                                                className={styles.upsellItemDiscount}
                                                                style={{
                                                                    color: "#2ea818",
                                                                    fontSize: "12px",
                                                                    fontWeight: "650",
                                                                }}
                                                            >
                                                                (
                                                                {getDiscountValueWithSign(
                                                                    product?.discountType,
                                                                    product?.discountValue,
                                                                    currency,
                                                                    money_format,
                                                                )}
                                                                )
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.addonActionWrapper}>
                                            {quantity === 0 ? (
                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="reviewBottomCta"
                                                >
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onAddProduct(product, selectedVariant);
                                                        }}
                                                        className={styles.addButton}
                                                        style={{
                                                            background:
                                                                bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                                            color: bundleConfig?.bundles_design?.add_to_cart_text_color,
                                                            border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                                            fontSize:
                                                                bundleConfig?.bundles_design?.add_to_cart_text_size ||
                                                                "14px",
                                                            fontWeight:
                                                                bundleConfig?.bundles_design?.add_to_cart_text_style?.split(
                                                                    " ",
                                                                )[0] || "600",
                                                            fontStyle:
                                                                bundleConfig?.bundles_design?.add_to_cart_text_style?.split(
                                                                    " ",
                                                                )[1] || "normal",
                                                        }}
                                                    >
                                                        {bundleConfig?.review_step?.addon_button_text || "Add"}
                                                    </button>
                                                </InspectorEditableBlock>
                                            ) : (
                                                <div className={styles.qtySelector}>
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onProductRemove(selectedVariant.id);
                                                        }}
                                                    >
                                                        −
                                                    </button>

                                                    <span>{quantity}</span>

                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onAddProduct(product, selectedVariant);
                                                        }}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* Variant Selector */}
                                    {product.variants.length > 1 && (
                                        <div className={styles.variantSelectorWrapper}>
                                            {renderVariantSelector(product)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {len >= 2 && (
                        <span onClick={nextSlide} style={{ cursor: "pointer" }}>
                            <AngleRightIcon size={15} color="#303030" />
                        </span>
                    )}
                </div>
                {/* Pagination Dots */}
                {len >= 2 && (
                    <div className={styles.dotsWrapperContainer}>
                        <div className={styles.dotsWrapper}>
                            {Array.from({ length: totalDots })
                                .slice(startDot, endDot)
                                .map((_, idx) => {
                                    const actualIndex = startDot + idx;
                                    return (
                                        <span
                                            style={{
                                                background:
                                                    actualIndex === currentDotIndex
                                                        ? bundleConfig?.bundles_design?.add_to_cart_bg_color || "#000"
                                                        : "gray",
                                            }}
                                            key={actualIndex}
                                            title={`Go to slide ${actualIndex + 1}`}
                                            className={`${styles.dot} ${
                                                actualIndex === currentDotIndex ? styles.activeDot : ""
                                            }`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setCurrentIndex(actualIndex * itemsPerView);
                                            }}
                                        />
                                    );
                                })}
                        </div>
                    </div>
                )}
            </div>
        </InspectorEditableBlock>
    );
};

export default ReviewAddOnCarousel;
